import React from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from 'react-query';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  alpha,
  Divider,
  Chip,
} from '@mui/material';
import {
  Event,
  Event as EventIcon,
  Person,
  Assignment,
  Add as AddIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  ViewModule as GridIcon,
  ViewDay as CalendarViewIcon,
  Store as StoreIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { apiWithAuth } from '../services/api';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getUnreadNotificationCount } from '../services/notificationService';
import Badge from '@mui/material/Badge';

// Set up the calendar localization
const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Notification Bell Component with Badge
const NotificationBellWithBadge = () => {
  const { data: unreadCount = 0 } = useQuery(
    'unreadNotificationCount',
    () => getUnreadNotificationCount(),
    {
      enabled: true,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  return (
    <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error">
      <NotificationsIcon />
    </Badge>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const { currentUser } = useAuth();
  const [viewMode, setViewMode] = React.useState('grid'); // 'grid' or 'calendar'
  const roleId = Number(currentUser?.role_id);

  // Fetch vendor applications and derive stats/events from real data
  const { data: vendorApplications = [], isLoading: appsLoading } = useQuery(
    'vendorApplications',
    async () => {
      const token = localStorage.getItem('token');
      const resp = await apiWithAuth(token).get('/applications/');
      return Array.isArray(resp.data) ? resp.data : [];
    },
    { enabled: roleId === 1 }
  );

  const derivedVendorStats = React.useMemo(() => {
    const pending = vendorApplications.filter(a => a.status?.toLowerCase() === 'pending').length;
    const accepted = vendorApplications.filter(a => a.status?.toLowerCase() === 'accepted').length;
    return { active_applications: pending, accepted_events: accepted };
  }, [vendorApplications]);

  const vendorEvents = React.useMemo(() => {
    // accepted applications' events
    return vendorApplications
      .filter(a => a.status?.toLowerCase() === 'accepted' && a.event)
      .map(a => ({
        id: a.event.event_id,
        title: a.event.title,
        event_date: a.event.date,
        description: a.comments || '',
      }));
  }, [vendorApplications]);

  // Organizer data: fetch real events and applications
  const { data: organizerEvents = [], isLoading: orgEventsLoading } = useQuery(
    'organizerEvents',
    async () => {
      const token = localStorage.getItem('token');
      const resp = await apiWithAuth(token).get('/organizer/events');
      return Array.isArray(resp.data) ? resp.data : [];
    },
    { enabled: roleId === 2 }
  );

  const { data: organizerApplications = [], isLoading: orgAppsLoading } = useQuery(
    ['organizerApplications', organizerEvents?.length],
    async () => {
      const token = localStorage.getItem('token');
      const api = apiWithAuth(token);
      const results = await Promise.all(
        organizerEvents.map(ev => api.get(`/events/${ev.event_id}/applications/`).then(r => r.data).catch(() => []))
      );
      return results.flat();
    },
    { enabled: roleId === 2 && organizerEvents.length > 0 }
  );

  const derivedOrganizerStats = React.useMemo(() => {
    const upcoming = organizerEvents.filter(ev => new Date(ev.date) >= new Date()).length;
    const pending = organizerApplications.filter(a => a.status?.toLowerCase() === 'pending').length;
    const uniqueVendors = new Set(organizerApplications.map(a => a.vendor_id)).size;
    return { upcoming, pending, totalVendors: uniqueVendors };
  }, [organizerEvents, organizerApplications]);

  // Convert events to calendar format
  const calendarEvents = React.useMemo(() => {
    if (!vendorEvents) return [];
    return vendorEvents.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.event_date),
      end: new Date(event.event_date),
      description: event.description,
      allDay: true,
    }));
  }, [vendorEvents]);

  // Custom calendar styles
  const calendarStyles = {
    calendar: {
      height: '600px',
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[1],
    },
    event: {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '2px 4px',
      fontSize: '0.875rem',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    today: {
      backgroundColor: theme.palette.primary.light,
    },
    toolbar: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    },
    header: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    month: {
      backgroundColor: theme.palette.background.paper,
    },
    day: {
      backgroundColor: theme.palette.background.paper,
    },
    week: {
      backgroundColor: theme.palette.background.paper,
    },
  };

  // Define menu items based on user role
  const getMenuItems = () => {
    if (Number(currentUser?.role_id) === 2) { // Organizer
      return [
        {
          title: 'My Events',
          description: 'Manage your created events',
          icon: <Event fontSize="large" color="primary" />,
          action: () => history.push('/my-events'),
          color: theme.palette.primary.main,
          stats: `${orgEventsLoading ? '-' : derivedOrganizerStats.upcoming} Events`,
        },
        {
          title: 'Profile',
          description: 'View and edit your profile information',
          icon: <Person fontSize="large" color="primary" />,
          action: () => history.push('/organizer-profile'),
          color: theme.palette.secondary.main,
          stats: 'Profile',
        },
      ];
    } else { // Vendor
      return [
        {
          title: 'Profile',
          description: 'View and edit your profile information',
          icon: <Person fontSize="large" color="primary" />,
          action: () => history.push('/vendor-profile'),
          color: theme.palette.secondary.main,
          stats: 'Profile',
        },
        {
          title: 'Applications',
          description: 'Manage your applications',
          icon: <Assignment fontSize="large" color="primary" />,
          action: () => history.push('/applications'),
          color: theme.palette.success.main,
          stats: `${derivedVendorStats.active_applications} Active`,
        },
        {
          title: 'Find Events',
          description: 'Browse and apply to events',
          icon: <Event fontSize="large" color="primary" />,
          action: () => history.push('/events'),
          color: theme.palette.primary.main,
          stats: 'Browse',
        },
      ];
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Modern Professional Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
            color: 'white',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' } }}>
                <Typography 
                  variant="h5" 
                  component="h1" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    mb: 1,
                    fontSize: { xs: '1.35rem', md: '1.5rem' },
                    lineHeight: 1.3
                  }}
                >
                  {Number(currentUser?.role_id) === 2 
                    ? `Welcome back, ${currentUser?.name || 'Organizer'}`
                    : `Welcome back, ${currentUser?.name || 'Vendor'}`
                  }
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.875rem'
                  }}
                >
                  {Number(currentUser?.role_id) === 2 
                    ? 'Manage your events and track vendor applications'
                    : 'Manage your applications and find new opportunities'
                  }
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {Number(currentUser?.role_id) === 1 && (
                  <Tooltip title="Notifications">
                    <IconButton 
                      sx={{ 
                        color: 'rgba(255,255,255,0.9)',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        minWidth: { xs: 48, md: 40 },
                        minHeight: { xs: 48, md: 40 },
                      }}
                      onClick={() => history.push('/notifications')}
                    >
                      <NotificationBellWithBadge />
                    </IconButton>
                  </Tooltip>
                )}
                {Number(currentUser?.role_id) === 2 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => history.push('/create-event')}
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      minHeight: { xs: 48, md: 40 },
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)',
                      }
                    }}
                  >
                    Create Event
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Key Metrics */}
        <Grid container spacing={{ xs: 2, md: 2 }} sx={{ mb: 3 }}>
          {Number(currentUser?.role_id) === 2 ? (
            // Organizer Stats
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                          {orgEventsLoading ? '-' : derivedOrganizerStats.upcoming}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                          Upcoming Events
                        </Typography>
                      </Box>
                      <CalendarIcon sx={{ fontSize: { xs: 28, md: 36 }, color: alpha('#2e7d32', 0.2) }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                          {orgAppsLoading ? '-' : derivedOrganizerStats.pending}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                          Pending Reviews
                        </Typography>
                      </Box>
                      <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 36 }, color: alpha('#ed6c02', 0.2) }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                          {orgAppsLoading ? '-' : derivedOrganizerStats.totalVendors}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                          Vendor Partners
                        </Typography>
                      </Box>
                      <GroupIcon sx={{ fontSize: { xs: 28, md: 36 }, color: alpha('#0288d1', 0.2) }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            // Vendor Stats
            <>
              <Grid item xs={12} sm={6} md={6}>
                <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        {appsLoading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                              {derivedVendorStats.active_applications}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                              Active Applications
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Assignment sx={{ fontSize: { xs: 28, md: 36 }, color: alpha('#1976d2', 0.2) }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Card elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        {appsLoading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                              {derivedVendorStats.accepted_events}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                              Accepted Events
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Event sx={{ fontSize: { xs: 28, md: 36 }, color: alpha('#2e7d32', 0.2) }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Events Section for Vendors */}
        {Number(currentUser?.role_id) === 1 && (
          <Paper elevation={1} sx={{ mb: 3, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: { xs: 20, md: 24 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>My Events</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Grid View">
                    <IconButton 
                      size={isMobile ? "medium" : "small"}
                      onClick={() => setViewMode('grid')}
                      sx={{ 
                        color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                        bgcolor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        minWidth: { xs: 48, md: 40 },
                        minHeight: { xs: 48, md: 40 },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      <GridIcon fontSize={isMobile ? "medium" : "small"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Calendar View">
                    <IconButton 
                      size={isMobile ? "medium" : "small"}
                      onClick={() => setViewMode('calendar')}
                      sx={{ 
                        color: viewMode === 'calendar' ? 'primary.main' : 'text.secondary',
                        bgcolor: viewMode === 'calendar' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        minWidth: { xs: 48, md: 40 },
                        minHeight: { xs: 48, md: 40 },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      <CalendarViewIcon fontSize={isMobile ? "medium" : "small"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {appsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : vendorEvents?.length > 0 ? (
                viewMode === 'grid' ? (
                  <Grid container spacing={2}>
                    {vendorEvents.map((event) => (
                      <Grid item xs={12} sm={6} md={4} key={event.id}>
                        <Card 
                          elevation={1}
                          sx={{ 
                            height: '100%',
                            transition: 'all 0.3s ease',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'white',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                            }
                          }}
                        >
                          <CardContent sx={{ p: 2.5 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                              {event.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {format(new Date(event.event_date), 'MMM d, yyyy')}
                              </Typography>
                            </Box>
                            {event.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {event.description}
                              </Typography>
                            )}
                          </CardContent>
                          <Divider />
                          <CardActions sx={{ p: 2 }}>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => history.push(`/event/${event.id}`)}
                              sx={{ 
                                textTransform: 'none',
                                borderRadius: 1
                              }}
                            >
                              View Details
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{
                      ...calendarStyles.calendar,
                      boxShadow: 'none',
                      border: 'none',
                    }}
                    eventPropGetter={(event) => ({
                      style: {
                        ...calendarStyles.event,
                        backgroundColor: theme.palette.primary.main,
                        border: 'none',
                        borderRadius: '4px',
                        padding: '2px 4px',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      },
                    })}
                    onSelectEvent={(event) => history.push(`/event/${event.id}`)}
                    components={{
                      event: ({ event }) => (
                        <Tooltip title={event.description || event.title}>
                          <div style={{ 
                            padding: '2px 4px',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                          }}>
                            {event.title}
                          </div>
                        </Tooltip>
                      ),
                    }}
                    messages={{
                      next: "Next",
                      previous: "Previous",
                      today: "Today",
                      month: "Month",
                      week: "Week",
                      day: "Day",
                      agenda: "Agenda",
                      date: "Date",
                      time: "Time",
                      event: "Event",
                      noEventsInRange: "No events in this range",
                      showMore: (total) => `+${total} more`,
                      allDay: "All Day",
                      work_week: "Work Week",
                      yesterday: "Yesterday",
                      tomorrow: "Tomorrow",
                      thisWeek: "This Week",
                      nextWeek: "Next Week",
                      lastWeek: "Last Week",
                      thisMonth: "This Month",
                      nextMonth: "Next Month",
                      lastMonth: "Last Month",
                    }}
                    culture="en-US"
                    popup
                    selectable
                    longPressThreshold={1}
                    showMultiDayTimes
                    step={30}
                    timeslots={2}
                    min={new Date(0, 0, 0, 8, 0, 0)} // Start at 8 AM
                    max={new Date(0, 0, 0, 20, 0, 0)} // End at 8 PM
                    defaultDate={new Date()}
                    views={['month', 'week', 'day']}
                    defaultView="month"
                  />
                )
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                  <Typography variant="body1" color="text.secondary">
                    No upcoming events found
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => history.push('/events')}
                    sx={{ mt: 2, borderRadius: 1 }}
                  >
                    Browse Events
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Quick Access Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {getMenuItems().map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3, textAlign: 'center' }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 2,
                      color: 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography gutterBottom variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1.5 }}>
                    {item.description}
                  </Typography>
                  <Chip 
                    label={item.stats} 
                    size="small" 
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </CardContent>
                <Divider />
                <CardActions sx={{ p: 2 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    onClick={item.action}
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 1
                    }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Actions for Organizers */}
        {Number(currentUser?.role_id) === 2 && (
          <Paper elevation={1} sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Quick Actions
              </Typography>
            </Box>
            <Box sx={{ p: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => history.push('/create-event')}
                startIcon={<AddIcon />}
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'none'
                }}
              >
                Create New Event
              </Button>
              <Button
                variant="outlined"
                onClick={() => history.push('/my-events')}
                startIcon={<Event />}
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'none'
                }}
              >
                View All Events
              </Button>
              <Button
                variant="outlined"
                onClick={() => history.push('/vendors')}
                startIcon={<StoreIcon />}
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'none'
                }}
              >
                Browse Vendors
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard; 